import { apiClient, unwrap } from '@/lib/apiClient';
import type { ApiSuccess, PaginationMeta } from '@/types/api';
import type { TicketCategory, TicketDetail, TicketStatus, TicketSummary } from '@/types/support';
import type { NewTicketPayload } from '../schemas/support.schema';

/** Files go as multipart; the browser adds the boundary, so Content-Type is left unset. */
function multipart(fields: Record<string, string>, files: File[]): FormData {
  const form = new FormData();
  for (const [name, value] of Object.entries(fields)) form.append(name, value);
  for (const file of files) form.append('attachments', file);
  return form;
}

const MULTIPART = { headers: { 'Content-Type': undefined } };

/** Every support call the institute panel makes (CLAUDE.md §6). */
export const supportApi = {
  /** `from` and `to` are calendar days, `YYYY-MM-DD`, both included. */
  list: async (params: {
    page: number;
    limit: number;
    status?: TicketStatus;
    category?: TicketCategory;
    from?: string;
    to?: string;
    search?: string;
  }) => {
    const response = await apiClient.get<ApiSuccess<{ items: TicketSummary[]; openCount: number }>>(
      '/institute/support/tickets',
      { params: Object.fromEntries(Object.entries(params).filter(([, value]) => value !== undefined)) },
    );
    const { items, openCount } = response.data.data;
    const meta: PaginationMeta = response.data.meta ?? {
      page: 1,
      limit: params.limit,
      total: items.length,
      totalPages: 1,
    };
    return { items, openCount, meta };
  },

  create: (payload: NewTicketPayload, files: File[]) =>
    unwrap(
      apiClient.post<ApiSuccess<TicketSummary>>(
        '/institute/support/tickets',
        multipart(
          {
            subject: payload.subject,
            category: payload.category,
            priority: payload.priority,
            message: payload.message,
          },
          files,
        ),
        MULTIPART,
      ),
    ),

  get: (uuid: string) =>
    unwrap(apiClient.get<ApiSuccess<TicketDetail>>(`/institute/support/tickets/${uuid}`)),

  reply: (uuid: string, message: string, files: File[]) =>
    unwrap(
      apiClient.post<ApiSuccess<TicketDetail>>(
        `/institute/support/tickets/${uuid}/replies`,
        multipart({ message }, files),
        MULTIPART,
      ),
    ),

  /** For `downloadPrivateFile` — fetched with the session, never a public link. */
  attachmentPath: (ticketUuid: string, attachmentUuid: string) =>
    `/institute/support/tickets/${ticketUuid}/attachments/${attachmentUuid}`,
};
